<script setup lang="ts">
import { ref, watch } from 'vue'
import SliderElement from '@vueform/slider'
import { useI18n } from 'vue-i18n'
import { Input } from '@/components/ui/input'
import { usePCSFilter } from '@/lib/usePCSFilter'
import { cn } from '@/lib/utils'
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'

const { t } = useI18n()

const props = defineProps<{
	checkboxTitle: string
	isOpenedInitially?: boolean
}>()

const { minPrice, maxPrice, defaultMaxPrice } = usePCSFilter()
const priceRange = ref([minPrice.value, maxPrice.value])
const isOpen = ref(props.isOpenedInitially || false)
const currentSelectedRangeOption = ref('')
const defaultRanges = [
	{
		text: t('pcs_filter_range_options_range1'),
		optionValue: 'range1',
		range: [0, 1000],
	},
	{
		text: t('pcs_filter_range_options_range2'),
		optionValue: 'range2',
		range: [1000, 9000],
	},
	{
		text: t('pcs_filter_range_options_range3'),
		optionValue: 'range3',
		range: [9000, defaultMaxPrice.value],
	},
]

const updateSelectedRangeOption = (newRange: number[]) => {
	const matchingRange = defaultRanges.find(
		({ range }) => range[0] === newRange[0] && range[1] === newRange[1],
	)

	currentSelectedRangeOption.value = matchingRange?.optionValue || ''
}

const setPriceRangeBasedOnSelection = (newOptionValue: string) => {
	const selectedRange = defaultRanges.find(
		range => range.optionValue === newOptionValue,
	)?.range
	if (selectedRange) {
		;[minPrice.value, maxPrice.value] = selectedRange
	}
}

const triggerUnfocus = (event: KeyboardEvent) => {
	const { target } = event
	if (target instanceof HTMLInputElement) {
		target.blur()
	}
}

const ensureValidInput = (event: KeyboardEvent, inputType: 'min' | 'max') => {
	const { target } = event
	if (!(target instanceof HTMLInputElement)) return

	const inputValue = Number(target.value)

	if (inputType === 'min' && inputValue > maxPrice.value) {
		target.value = '0'
		return
	}

	if (!target.validity.valid) {
		target.value =
			inputValue > defaultMaxPrice.value
				? defaultMaxPrice.value.toString()
				: '0'
	} else {
		target.value = target.value.replace(/^0+/, '') || '0'
	}
}

watch(minPrice, newVal => {
	if (newVal > maxPrice.value) {
		// Should set minPrice to 0 if maxPrice is smaller than minPrice according to the design
		priceRange.value = [0, priceRange.value[1]]
	} else {
		priceRange.value = [newVal, priceRange.value[1]]
	}
})
watch(maxPrice, newVal => {
	if (newVal < minPrice.value) {
		// Should set minPrice to 0 if maxPrice is smaller than minPrice according to the design
		priceRange.value = [0, newVal]
	} else {
		priceRange.value = [priceRange.value[0], newVal]
	}
})
watch(
	priceRange,
	newRange => {
		;[minPrice.value, maxPrice.value] = newRange
		updateSelectedRangeOption(newRange)
	},
	{ immediate: true },
)

watch(currentSelectedRangeOption, newVal => {
	setPriceRangeBasedOnSelection(newVal)
})
</script>

<template>
	<Collapsible v-model:open="isOpen" class="border-b border-n-100">
		<div class="mb-16px flex items-center">
			<label
				:for="checkboxTitle"
				class="typography-s2-emp mr-8px cursor-pointer select-none whitespace-nowrap capitalize"
			>
				{{ checkboxTitle }}
			</label>

			<CollapsibleTrigger
				class="flex w-full cursor-pointer lg:order-last lg:justify-end"
			>
				<div
					:class="
						cn('bs-new-icon icon-black-chevron h-24px w-24px rotate-180', {
							'rotate-0': isOpen,
						})
					"
				></div>
			</CollapsibleTrigger>
		</div>

		<CollapsibleContent class="my-24px">
			<div class="mb-24px flex flex-col gap-16px">
				<div class="flex items-center justify-center gap-8px p-4px">
					<div class="relative w-full">
						<Input
							type="number"
							min="0"
							:max="defaultMaxPrice"
							v-model="minPrice"
							class="typography-b2-reg border-n-100 py-[10px] pl-48px pr-16px focus-visible:ring-2 focus-visible:ring-o-300 lg:pl-[26px]"
							lazy
							@keyup.enter="triggerUnfocus"
							:oninput="
								(event: KeyboardEvent) => ensureValidInput(event, 'min')
							"
						/>
						<div
							class="typography-b2-reg pointer-events-none absolute left-16px top-0 flex h-1/2 w-full translate-y-1/2 items-center"
						>
							<span class="lg:hidden">HK</span>
							<span>$</span>
						</div>
					</div>

					<span class="h-[1px] w-24px bg-n-100"></span>

					<div class="relative w-full">
						<Input
							type="number"
							min="0"
							:max="defaultMaxPrice"
							v-model="maxPrice"
							class="typography-b2-reg border-n-100 py-[10px] pl-48px pr-16px focus-visible:ring-2 focus-visible:ring-o-300 lg:pl-[26px]"
							lazy
							@keyup.enter="triggerUnfocus"
							:oninput="
								(event: KeyboardEvent) => ensureValidInput(event, 'max')
							"
						/>
						<div
							class="typography-b2-reg pointer-events-none absolute left-16px top-0 flex h-1/2 w-full translate-y-1/2 items-center"
						>
							<span class="lg:hidden">HK</span>
							<span>$</span>
						</div>
					</div>
				</div>
				<SliderElement
					class="pl-[18px] pr-[10px]"
					:format="{ prefix: '$', thousand: ' ' }"
					:step="500"
					:max="defaultMaxPrice"
					v-model="priceRange"
				/>
			</div>

			<RadioGroup
				v-model="currentSelectedRangeOption"
				class="typography-s2-reg flex flex-col gap-close-friends"
			>
				<template v-for="item in defaultRanges" :key="item.optionValue">
					<div class="flex items-center gap-16px">
						<Label class="w-full cursor-pointer" :for="item.optionValue">
							{{ item.text }}
						</Label>
						<RadioGroupItem
							class="lg:order-first"
							:id="item.optionValue"
							:value="item.optionValue"
						/>
					</div>
				</template>
			</RadioGroup>
		</CollapsibleContent>
	</Collapsible>
</template>
